import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Certificate, Vessel, VesselsService } from '../../../vessels.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentReference, DocumentService } from 'src/app/documents/documents.service';
import { PgMessagesService, MessageLevel } from 'src/app/shared/services/pg-messages.service';
import { DateTimeHelpers } from 'src/app/shared/helpers/date-time.helpers';
import moment from 'moment';
import { saveAs } from 'file-saver';
import { SharedModule } from 'src/app/shared/shared.module';
import { forkJoin } from 'rxjs';
import { MaterialAutoCompleteComponent } from 'src/app/shared/components/material-autocomplete/material-auto-complete.component';
import { MaterialDateRangeComponent, DateRange } from 'src/app/shared/components/material-date-picker/material-date-range.component';
import { MaterialChunkFileUploadComponent } from 'src/app/shared/components/material-file-uploader/material-chunk-file-upload.component';
import { MatIconModule } from '@angular/material/icon';
import { MaterialPdfViewerComponent } from 'src/app/shared/components/material-pdf-viewer/material-pdf-viewer.component';
import { MaterialPgButtonComponent } from 'src/app/shared/components/material-pg-button/material-pg-button.component';
import { Versioned } from 'src/app/shared/helpers/versioned';
import { LookupServiceSpa } from 'src/app/generated/services/lookups/lookupSpa';

@Component({
  selector: 'app-certificate',
  imports: [
    SharedModule,
    MaterialAutoCompleteComponent,
    MaterialDateRangeComponent,
    MaterialChunkFileUploadComponent,
    MatIconModule,
    MaterialPdfViewerComponent,
    MaterialPgButtonComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './certificate.component.html',
})
export class CertificateComponent implements OnInit{
  versionedVessel!: Versioned<Vessel>;
  certificate = Certificate.create();
  name: { name: string; abbr: string } = { name: '', abbr: '' };
  fileId!: string;
  history: any[] = [];
  issueDate!: DateRange;
  expirationDate!: DateRange;
  certificateList: { name: string; abbr: string }[] = [];
  moment = moment;
  editMode = false;
  title = new EventEmitter<string>();
  availableCertificates!: { name: string; abbr: string }[];
  doc?: DocumentReference;

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _vesselsService: VesselsService,
    private _documentsService: DocumentService,
    private _pgMessagesService: PgMessagesService,
    private _lookupServiceSpa: LookupServiceSpa,
  ) {}

  ngOnInit(): void {
    this._route.params.subscribe(params => {
      const imo = params['imo'];
      if (!imo) {return ;}

      this.certificate = {
        name: '',
        abbr: '',
        history: [],
        info: {
          issueDate: new Date().getTime(),
          expirationDate: new Date().getTime(),
          fileId: ''
        }
      };

      forkJoin([
        this._vesselsService.getVessel(imo),
        this._lookupServiceSpa.getLookupItems({categoryName: 'vesselCertificate'})
      ]).subscribe(results => {
        this.versionedVessel = results[0];
        this.certificateList = results[1].items.map((item: any) => ({
          name: item.values[0],
          abbr: item.values[1]
        }));
        this.title.emit(this.versionedVessel?.data?.name?.name + ' Certificate');
        this.availableCertificates = this.findAvailableCertificate()!;
        if(params['name']) {
          this.editMode = true;
          const cert = this.versionedVessel.data!.certificates.find(c => c.name === params['name']);
          if (cert) {
            this.certificate = cert;
            this.name = { name: cert.name, abbr: cert.abbr };
            this.fileId = cert.info!.fileId!;
            this.issueDate = { startDate: new Date(cert.info!.issueDate), endDate: new Date(cert.info!.issueDate) };
            this.expirationDate = { startDate: new Date(cert.info!.expirationDate), endDate: new Date(cert.info!.expirationDate) };
            this.history = cert.history ? [...cert.history] : [];
          }
        }
      });
    });
  }

  findAvailableCertificate() {
    if (this.certificateList && this.versionedVessel && this.versionedVessel?.data?.certificates) {
      return this.certificateList.filter(c => {
        const alreadyExists = this.versionedVessel.data!.certificates.some(vc => vc.name === c.name);
        if (this.editMode && c.name === this.certificate?.name) {
          return true;
        }
        return !alreadyExists;
      });
    }
  }

  certificateFormatter(certificate: { name: string; abbr: string }) {
    return `${certificate.name} - ${certificate.abbr}`;
  }

  saveCertificate() {
    if (!this.issueDate?.startDate || !this.expirationDate?.startDate) {
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Error,
        topic: 'Certificate',
        message: 'Issue Date and Expiration Date are required.'
      });
      return;
    }

    if (this.issueDate.startDate.getTime() >= this.expirationDate.startDate.getTime()) {
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Error,
        topic: 'Certificate',
        message: 'Issue Date must be before Expiration Date.'
      });
      return;
    }
    this.certificate.history = this.certificate.history || [];
    this.certificate.info = {
      issueDate: this.issueDate.startDate.getTime(),
      expirationDate: this.expirationDate.startDate.getTime(),
      fileId: this.fileId,
    };

    if (!this.editMode) {
      this.versionedVessel?.data?.certificates.push(this.certificate);
      this.certificate = {
        name: '',
        abbr: '',
        history: [],
        info: {
          issueDate: new Date().getTime(),
          expirationDate: new Date().getTime(),
          fileId: ''
        }
      };
    } else {

      const index = this.versionedVessel?.data?.certificates.findIndex(
        cert => cert.name === this.certificate.name
      );

      if (index !== -1) {
        this.certificate.history.push({ ...this.certificate.info });
        this.versionedVessel.data!.certificates[index!] = this.certificate;
      }
    }
    this._vesselsService.updateVessel(this.versionedVessel).subscribe(
      () => {
        this.availableCertificates = this.findAvailableCertificate()!;
        this._pgMessagesService.publishMessage({
          level: MessageLevel.Info,
          topic: 'Certificate',
          message: `Certificate ${this.certificate.name} updated successfully.`
        });
      },
      () => this._pgMessagesService.publishMessage({
        level: MessageLevel.Error,
        topic: 'Certificate',
        message: 'Error updating certificate.'
      })
    );
  }

  uploadCertificateFile(event: any) {
    if (event && event.id) {
      this.fileId = event.id;
    }}

  cancel() {
    this.backToVessel();
  }

  backToVessel() {
    this._router.navigate(['/vessel', this.versionedVessel?.data?.imo]);
  }

  downloadFile(fileId: string, prefix: string) {
    this._documentsService.download(fileId).subscribe(
      (b) => saveAs(b.body, `${prefix}.pdf`),
      () => this._pgMessagesService.publishMessage({
        message: 'Could not download this file.',
        level: MessageLevel.Error,
        topic: 'Certificate',
      })
    );
  }

  dateTime(timestamp: number) {
    return DateTimeHelpers.formatLocalDate(timestamp);
  }
}
